import i18nFactory from '@yandex-int/i18n';

import { Size, Tld } from '../../../typings/common';
import {
    Bundle,
    CommonCreativeApiItem,
    CommonCreativeApiResponse,
    CommonCreativeBaseApiItem,
    CommonCreativeIdeaApiItem,
    CommonCreativeIdeaApiResponse,
    CommonCreativeOptions,
    CommonCreativePresetApiItem,
    CommonCreativePresetApiResponse,
    DisclaimersApiResponse,
    MediaSets
} from '../../common/requestsTypings';
import {
    CommonOptionGroupName,
    CommonOptionName,
    CreativeOptionGroupApiValue,
    CreativeOptionGroupName,
    CreativeOptionsConfig,
    CreativeOptionsData,
    CreativeOptionsGroupValue,
    CreativeOptionsValue,
    CreativeOptionType,
    CreativeOptionValue
} from '../../CreativeOptions/typings';
import { getCommonConfig } from '../CreativeConfigHelper/CreativeConfigHelper';
import { getDisclaimerItem, getMediaSet } from '../helpers';
import { CommonCreativeItem } from '../typings';

// tslint:disable-next-line no-external-i18n-import
import * as keyset from './AddCommonCreative.i18n';

const i18n = i18nFactory(keyset);

function getApiElements(elements: CreativeOptionsValue, cropSize: Size): CreativeOptionGroupApiValue[] {
    return elements.map((value: CreativeOptionsGroupValue) => {
        switch (value.type) {
            case CommonOptionGroupName.logo:
                return {
                    type: CommonOptionGroupName.logo,
                    available: Boolean(value.visible),
                    options: {
                        width: value.options.width,
                        height: value.options.height
                    },
                    mediaSet:
                        value.mediaSet && value.options[CommonOptionName.fileId] !== null ? value.mediaSet : undefined
                };

            case CommonOptionGroupName.image:
                return {
                    type: CommonOptionGroupName.image,
                    available: Boolean(value.visible),
                    options: {
                        width: cropSize.width,
                        height: cropSize.height
                    },
                    mediaSet:
                        value.mediaSet && value.options[CommonOptionName.fileId] !== null ? value.mediaSet : undefined
                };

            default:
                const options = { ...value.options };

                if (options[CommonOptionName.placeholder]) {
                    delete options[CommonOptionName.placeholder];

                    if (!options[CommonOptionName.content]) {
                        options[CommonOptionName.content] = '';
                    }
                }

                return {
                    type: value.type,
                    available: Boolean(value.visible),
                    options
                };
        }
    });
}

function getApiMediaSets(mediaSets: MediaSets, elements: CreativeOptionsValue): MediaSets {
    const fileElements = elements.filter(value => Boolean(value.mediaSet));

    return Object.keys(mediaSets).reduce(
        (result, key): MediaSets => {
            const element = fileElements.find(value => value.mediaSet === key);

            if (!element || element.options[CommonOptionName.fileId] === null) {
                return result;
            }

            result[key] = mediaSets[key];

            return result;
        },
        {} as MediaSets
    );
}

export const mapCommonCreativeItemToApi = (
    item: CommonCreativeItem,
    bundle: Bundle,
    elements: CreativeOptionsValue,
    isAdaptive?: boolean
): CommonCreativeBaseApiItem => {
    const commonOptions = elements.find(el => el.type === CommonOptionGroupName.common);
    const filteredElements = elements.filter(el => el.type !== CommonOptionGroupName.common);

    const options: CommonCreativeOptions = {
        isAdaptive: Boolean(isAdaptive)
    };

    if (commonOptions) {
        options.hasAnimation = true;

        if (commonOptions.options[CommonOptionName.backgroundColor] !== undefined) {
            options.backgroundColor = String(commonOptions.options[CommonOptionName.backgroundColor]);
        }

        if (commonOptions.options[CommonOptionName.borderColor] !== undefined) {
            options.borderColor = String(commonOptions.options[CommonOptionName.borderColor]);
        }

        if (commonOptions.options[CommonOptionName.minWidth] !== undefined) {
            options.minWidth = Number(commonOptions.options[CommonOptionName.minWidth]);
        }

        if (commonOptions.options[CommonOptionName.minHeight] !== undefined) {
            options.minHeight = Number(commonOptions.options[CommonOptionName.minHeight]);
        }
    }

    return {
        width: item.data.width,
        height: item.data.height,
        bundle,
        elements: getApiElements(filteredElements, item.cropSize),
        mediaSets: getApiMediaSets(item.mediaSets, filteredElements),
        options
    };
};

export const mapCommonCreativeItemsToApi = (
    values: CreativeOptionsValue,
    bundle: Bundle,
    items: CommonCreativeItem[],
    presetId?: string,
    batchId?: string,
    isAdaptive?: boolean
): {
    available: boolean;
    presetId?: string;
    batchId?: string;
    data: CommonCreativeBaseApiItem;
}[] =>
    items.map(item => ({
        available: item.selected,
        presetId,
        batchId,
        data: mapCommonCreativeItemToApi(item, bundle, values, isAdaptive)
    }));

const getOptionsValueFromApiOptions = (
    options: CommonCreativeOptions,
    commonOptionsAvailable?: boolean
): Record<string, CreativeOptionValue> | undefined => {
    if (!commonOptionsAvailable) {
        return undefined;
    }

    const result: Record<string, CreativeOptionValue> = {
        [CommonOptionName.backgroundColor]: options.backgroundColor || '',
        [CommonOptionName.borderColor]: options.borderColor || ''
    };

    if (options.minWidth) {
        result[CommonOptionName.minWidth] = String(options.minWidth);
    }
    if (options.minHeight) {
        result[CommonOptionName.minHeight] = String(options.minHeight);
    }

    return result;
};

export const mapOptionsValueFromApi = (data: {
    values: CreativeOptionGroupApiValue[];
    config: CreativeOptionsConfig;
    commonOptions: CommonCreativeOptions;
    mediaSets: MediaSets;
    commonOptionsAvailable?: boolean;
    hiddenGroups?: CreativeOptionGroupName[];
}): CreativeOptionsValue => {
    const { values, config, commonOptions, mediaSets, commonOptionsAvailable = true, hiddenGroups } = data;

    const result: CreativeOptionsValue = values.map(value => {
        const groupConfig = config[value.type];
        const visible = hiddenGroups && hiddenGroups.includes(value.type) ? false : value.available;

        switch (value.type) {
            case CommonOptionGroupName.image:
                return {
                    type: CommonOptionGroupName.image,
                    visible,
                    options: {
                        width: value.options.width,
                        height: value.options.height,
                        // если задан id mediaSet'а, значит есть файловый параметр,
                        // в который нужно подставить id файлового ресурса
                        [CommonOptionName.fileId]: value.mediaSet
                            ? getMediaSet(mediaSets, value.mediaSet).fileId || null
                            : null
                    },
                    mediaSet: value.mediaSet
                };

            case CommonOptionGroupName.logo:
                return {
                    type: CommonOptionGroupName.logo,
                    visible,
                    options: {
                        width: value.options.width,
                        height: value.options.height,
                        // если задан id mediaSet'а, значит есть файловый параметр,
                        // в который нужно подставить id файлового ресурса
                        [CommonOptionName.fileId]: value.mediaSet
                            ? getMediaSet(mediaSets, value.mediaSet).fileId || null
                            : null
                    },
                    mediaSet: value.mediaSet
                };

            case CommonOptionGroupName.disclaimer:
                const options = Object.keys(value.options).reduce(
                    (filteredOptions: Record<string, CreativeOptionValue>, key: string) => {
                        if (
                            key === CommonOptionName.disclaimer ||
                            key === CommonOptionName.disclaimerCountry ||
                            Boolean(groupConfig.options.find(el => el.name === key))
                        ) {
                            filteredOptions[key] = value.options[key];
                        }

                        return filteredOptions;
                    },
                    {}
                );

                // для дисклеймера в пресетах с бека не приходит поле content, поэтому выставляем его сами
                if (!options[CommonOptionName.content]) {
                    const disclaimerConfig = groupConfig.options.find(
                        option => option.type === CreativeOptionType.Disclaimer
                    );

                    if (disclaimerConfig && disclaimerConfig.type === CreativeOptionType.Disclaimer) {
                        const disclaimer = getDisclaimerItem(
                            disclaimerConfig.items,
                            options[CommonOptionName.disclaimerCountry],
                            options[CommonOptionName.disclaimer]
                        );

                        if (disclaimer) {
                            options[CommonOptionName.content] = disclaimer.name;
                        }
                    }
                }

                return {
                    type: value.type,
                    visible,
                    options
                };

            default:
                return {
                    type: value.type,
                    visible,
                    options: Object.keys(value.options).reduce((filteredOptions, key) => {
                        if (
                            key === CommonOptionName.placeholder ||
                            Boolean(groupConfig.options.find(el => el.name === key))
                        ) {
                            filteredOptions[key] = value.options[key];
                        }

                        return filteredOptions;
                    }, {}),
                    maxLength: value.maxLength
                };
        }
    });

    const parsedCommonOptions = getOptionsValueFromApiOptions(commonOptions, commonOptionsAvailable);
    if (parsedCommonOptions) {
        // вторым элементов в конфиг вставляем параметры цвета рамки и фона баннера
        result.splice(1, 0, {
            type: CommonOptionGroupName.common,
            options: parsedCommonOptions
        });
    }

    return result;
};

export const mapCommonCreativeItemFromApi = (item: CommonCreativeApiItem): CommonCreativeItem => ({
    id: Number(item.id),
    selected: item.available,
    data: {
        width: item.data.width,
        height: item.data.height
    },
    mediaSets: item.data.mediaSets,
    cropSize: {
        width: Number(item.data.elements[0].options.width),
        height: Number(item.data.elements[0].options.height)
    }
});

export const mapCommonCreativeItemFromIdeaApi = (item: CommonCreativeIdeaApiItem): CommonCreativeItem => ({
    id: Number(item.id),
    selected: true,
    data: {
        width: item.data.width,
        height: item.data.height
    },
    mediaSets: item.data.mediaSets,
    cropSize: {
        width: Number(item.data.elements[0].options.width),
        height: Number(item.data.elements[0].options.height)
    }
});

export const mapCommonCreativeItemFromPresetApi = (item: CommonCreativePresetApiItem): CommonCreativeItem => ({
    id: Number(item.id),
    selected: true,
    data: {
        width: item.width,
        height: item.height
    },
    mediaSets: item.mediaSets,
    cropSize: {
        width: Number(item.elements[0].options.width),
        height: Number(item.elements[0].options.height)
    }
});

const mapCommonCreativeItemsFromApi = (response: CommonCreativeApiResponse): CommonCreativeItem[] =>
    response.items.map(mapCommonCreativeItemFromApi);

const mapCommonCreativeItemsFromIdeaApi = (response: CommonCreativeIdeaApiResponse): CommonCreativeItem[] =>
    response.items.map(mapCommonCreativeItemFromIdeaApi);

const mapCommonCreativeItemsFromPresetApi = (response: CommonCreativePresetApiResponse): CommonCreativeItem[] =>
    response.items.map(mapCommonCreativeItemFromPresetApi);

export const mapCommonCreativeOptionsFromApi = (
    data: CommonCreativeBaseApiItem,
    id: string,
    name: string,
    disclaimers: DisclaimersApiResponse,
    tld: Tld,
    commonOptionsAvailable?: boolean
): CreativeOptionsData => {
    const optionsConfig = getCommonConfig(id, disclaimers, tld);
    return {
        name,
        optionsConfig,
        optionsValue: mapOptionsValueFromApi({
            values: data.elements,
            config: optionsConfig,
            commonOptions: data.options,
            mediaSets: data.mediaSets,
            commonOptionsAvailable
        })
    };
};

export const mapCommonCreativeFromIdeaApi = (
    response: CommonCreativeIdeaApiResponse,
    disclaimers: DisclaimersApiResponse,
    tld: Tld,
    commonOptionsAvailable?: boolean
): {
    options: CreativeOptionsData;
    items: CommonCreativeItem[];
    bundle: Bundle;
    isIdea?: boolean;
} => {
    // значения параметров у всех размеров баннеров одинаковые, за исключение cropParameters и id кропнутых файлов
    // поэтому достаточно вытащить значение из первого элемента
    const item = response.items[0];

    return {
        options: mapCommonCreativeOptionsFromApi(
            item.data,
            response.ideaId,
            i18n('default-name'),
            disclaimers,
            tld,
            commonOptionsAvailable
        ),
        items: mapCommonCreativeItemsFromIdeaApi(response),
        bundle: item.data.bundle,
        isIdea: true
    };
};

export const mapCommonCreativeFromApi = (
    response: CommonCreativeApiResponse,
    disclaimers: DisclaimersApiResponse,
    tld: Tld,
    commonOptionsAvailable?: boolean
): {
    options: CreativeOptionsData;
    items: CommonCreativeItem[];
    bundle: Bundle;
    presetId?: string;
    batchId?: string;
} => {
    // значения параметров у всех размеров баннеров одинаковые, за исключением cropParameters и id кропнутых файлов
    // поэтому достаточно вытащить значение из первого элемента
    const item = response.items[0];

    return {
        options: mapCommonCreativeOptionsFromApi(
            item.data,
            response.id,
            response.name,
            disclaimers,
            tld,
            commonOptionsAvailable
        ),
        items: mapCommonCreativeItemsFromApi(response),
        bundle: item.data.bundle,
        presetId: item.presetId !== undefined ? String(item.presetId) : undefined,
        // Для некоторых креативов в запросе получения информации о них нет presetId,
        // необходимого для запроса создания. Для таких креативов есть batchId, который необходимо
        // сохранять и передавать затем в запросе создания
        batchId: item.batchId
    };
};

export const mapCommonCreativeFromPresetApi = (
    response: CommonCreativePresetApiResponse,
    disclaimers: DisclaimersApiResponse,
    tld: Tld,
    commonOptionsAvailable?: boolean
): {
    options: CreativeOptionsData;
    items: CommonCreativeItem[];
    bundle: Bundle;
} => {
    // значения параметров у всех размеров баннеров одинаковые, за исключение cropParameters и id кропнутых файлов
    // поэтому достаточно вытащить значение из первого элемента
    const item = response.items[0];

    return {
        options: mapCommonCreativeOptionsFromApi(
            item,
            String(response.id),
            i18n('default-name'),
            disclaimers,
            tld,
            commonOptionsAvailable
        ),
        items: mapCommonCreativeItemsFromPresetApi(response),
        bundle: item.bundle
    };
};
